/**
 * @file 素材图片处理相关函数
*/
import {BASE_WIDTH, BASE_HEIGHT, CHARCTER_BASE_SIZE, VIDEO_BASE_SIZE} from '../config';

/**
 * @description 获取图片的宽高
 * @input imgUrl, 素材宽elementW
 * @output 根据宽高比计算出元素h属性 elementH
*/
export const analyseImg = (url, element = {}) => {
    const {w = 0.8, h} = element;
    if (!url) {
        return Promise.resolve({});
    }
    if (h) {
        // 有图片高度就不分析了
        return Promise.resolve({
            ...element,
            elementW: w,
            elementH: h
        });
    }
    return new Promise((resolve, reject) => {
        // 创建对象
        const img = new Image();
        // 改变图片的src
        img.src = url;
        // 定时执行获取宽高
        const check = () => {
            // 只要任何一方大于0 表示服务器已经返回宽高
            // 这个可能比图片要早
            if (img.width > 0 || img.height > 0) {
                clearInterval(timer);
                resolve({
                    width: img.width,
                    height: img.height,
                    rate: img.width / img.height,
                    elementH: w * BASE_WIDTH * img.height / img.width / BASE_HEIGHT,
                    elementW: w
                });
            }
        };
        const timer = setInterval(check, 40);
        // 加载完成获取宽高
        img.onload = () => {
            clearInterval(timer);
            if (img.width > 0 || img.height > 0) {
                resolve({
                    width: img.width,
                    height: img.height,
                    rate: img.width / img.height,
                    elementH: w * BASE_WIDTH * img.height / img.width / BASE_HEIGHT,
                    elementW: w
                });
            }
            else {
                resolve({});
            }
        };
        img.onerror = () => {
            clearInterval(timer);
            resolve({});
        };
    });
};

/**
 * @description 获得合适的素材位置
 * @input 初始化的positionInfo
 * @output 一个可以完全展示在画布中的positionInfo
*/
export const calculatePosition = async (positionInfo, url) => {
    try {
        let {w, h, x, y} = positionInfo;
        if (+h + +y < 1) {
            return positionInfo;
        }
        if (+h < 0.99) {
            // 贴边容易bug 留个buffer 0.01
            y = 1 - h;
            return {
                ...positionInfo,
                y
            };
        }
        let rate = w / h;
        if (!rate && url) {
            // 数据有误重新算
            const analyseData = await analyseImg(url, {});
            rate = analyseData.rate;
        }
        h = 0.9;
        y = 0.05;
        w = 0.9 * rate;
        return {
            ...positionInfo,
            h,
            y,
            w
        };
    }
    catch (e) {
        return positionInfo;
    }
};

/**
 * @description 计算一个数字人的位置信息
 * @input 数字人信息，布局位置
 * @output 一个可以完全展示在画布中的positionInfo
 * https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/qCCPuIpbfn/34tQXKNlil/a3c4c2622e424b
*/
export const getCharacterPosition = async (character, type = 'default') => {
    try {
        // materialSize -> 添加数字人使用； originW，originH -> 兼容数字人母版
        let {materialSize: {width = 0, height = 0} = {}, posture = 0, w: originW, h: originH, url} = character;
        // 图片数据未加载 走失败逻辑
        if ((width === 0 || height === 0 || width === null || height === null) && !originH) {
            return CHARCTER_BASE_SIZE;
        }
        const formatH = BASE_WIDTH / BASE_HEIGHT;
        const rate = (height && width) ? (height / width) : (originH / originW / formatH);
        // posture 1：坐姿 2：站姿； 兜底：宽高大于2.2判断为站姿
        posture = posture || (rate > 2.2 ? 2 : 1);
        let {x, y, w} = CHARCTER_BASE_SIZE;
        let h = w * rate;
        // 视口转化 maskWidth = 0.09
        if (posture === 1) {
            switch (type) {
                case 'default':
                    w = 0.82;
                    h = w * rate * formatH;
                    x = 0.09;
                    y = 1 - h;
                    break;
                case 'left':
                    w = 0.45;
                    h = w * rate * formatH;
                    x = 0.09;
                    y = 1 - h - 0.15;
                    break;
                case 'right':
                    w = 0.45;
                    h = w * rate * formatH;
                    x = 0.46;
                    y = 1 - h - 0.15;
                    break;
                case 'center':
                    w = 0.58;
                    h = w * rate * formatH;
                    x = 0.21;
                    y = (1 - h) / 2;
                    break;
                default:
            }
        }
        if (posture === 2) {
            w = 0.55;
            h = w * rate * formatH;
            y = 1 - h;
            switch (type) {
                case 'default':
                    w = 0.82;
                    h = w * rate * formatH;
                    x = 0.09;
                    y = 1 - h;
                    break;
                case 'center':
                    x = 0.225;
                    break;
                case 'left':
                    x = 0.09;
                    break;
                case 'right':
                    x = 0.36;
                    break;
                default:
            }
        }
        return await calculatePosition({w, h, x, y}, url);
    }
    catch (e) {
        return CHARCTER_BASE_SIZE;
    }
};

/**
 * @description 新增元素碰撞检测 获得合适的素材位置
 * @input 元素列表、新增元素、类型
 * @output 尽可能无碰撞的一个位置
*/
export const getElementPosition = (elementList, target, type) => {
    try {
        const newTarget = {...target};
        let {y: targetY, h: targetH} = newTarget;
        const usedRange = elementList
            .filter(item => item.type === type && item.name)
            .map(item => {
                const {y = 0.1, h = 0.1} = item;
                return [Number(y), Number(y) + Number(h)];
            })
            .sort((a, b) => a[0] - b[0]);

        for (let i = 0; i < usedRange.length; i++) {
            const curRange = usedRange[i];
            const prevRange = usedRange[i - 1];
            if (i === 0) {
                if ((Number(targetY) + Number(targetH)) < Number(curRange[0])) {
                    break;
                }
                else {
                    targetY = curRange[1];
                }
            }
            else if ((prevRange[1] + Number(targetH)) < Number(curRange[0])) {
                break;
            }
            else {
                targetY = curRange[1];
            }
        }
        // 兜底 放最下面
        targetY = Math.min(Number(targetY), 1 - Number(targetH));

        newTarget.y = targetY;

        return newTarget;
    }
    catch (e) {
        return target;
    }
};

/**
 * @description 通过图片文件的file获取图片尺寸
 */
export function calculateImageSize(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.addEventListener('loadend', (e) => {
            const imageSrc = e.target.result;
            try {
                const image = new Image();
                image.addEventListener('load', function (e) {
                    resolve({
                        width: e.target.width,
                        height: e.target.height
                    });
                });
                image.addEventListener('error', function () {
                    reject();
                });
                image.src = imageSrc;
            }
            catch {
                reject();
            }
        });
    });
}

